import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export const allocationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/allocations',
  component: AllocationsPage,
});

function currentMonth(): string {
  const now = new Date();
  const istMs = now.getTime() + (5 * 60 + 30) * 60 * 1000;
  const d = new Date(istMs);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function previousMonth(m: string): string {
  const [y, mo] = m.split('-').map(Number);
  const yy = mo === 1 ? (y as number) - 1 : (y as number);
  const mm = mo === 1 ? 12 : (mo as number) - 1;
  return `${yy}-${String(mm).padStart(2, '0')}`;
}

function AllocationsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [month, setMonth] = React.useState(currentMonth());
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [statusMessage, setStatusMessage] = React.useState<string | null>(null);

  const matrixQuery = useQuery({
    queryKey: ['allocations', month],
    queryFn: () => api.getAllocationMatrix(month),
  });
  const peopleQuery = useQuery({
    queryKey: ['people-simple'],
    queryFn: () => api.listPeopleSimple(),
  });
  const projectsQuery = useQuery({
    queryKey: ['projects', 'active'],
    queryFn: () => api.listProjects({ status: 'active' }),
  });

  const cells = matrixQuery.data?.cells ?? [];
  const people = peopleQuery.data ?? [];
  const projects = projectsQuery.data ?? [];

  const [draft, setDraft] = React.useState<Record<string, number>>({});
  React.useEffect(() => {
    const initial: Record<string, number> = {};
    for (const c of cells) initial[`${c.personId}|${c.projectId}`] = c.percent;
    setDraft(initial);
  }, [matrixQuery.data]);

  const lockedByPerson = React.useMemo(() => {
    const s = new Set<string>();
    for (const c of cells) if (c.locked) s.add(c.personId);
    return s;
  }, [cells]);

  const setCell = (personId: string, projectId: string, value: number): void => {
    const key = `${personId}|${projectId}`;
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const rowSum = (personId: string): number => {
    let sum = 0;
    for (const p of projects) sum += draft[`${personId}|${p.id}`] ?? 0;
    return sum;
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const rows: { personId: string; projectId: string; percent: number }[] = [];
      for (const person of people) {
        if (lockedByPerson.has(person.id)) continue;
        for (const p of projects) {
          const percent = draft[`${person.id}|${p.id}`] ?? 0;
          if (percent > 0) rows.push({ personId: person.id, projectId: p.id, percent });
        }
      }
      return api.upsertAllocations({ month, rows });
    },
    onSuccess: async () => {
      setErrorMessage(null);
      setStatusMessage('Saved.');
      await queryClient.invalidateQueries({ queryKey: ['allocations', month] });
    },
    onError: (err) => {
      setStatusMessage(null);
      setErrorMessage(toMessage(err));
    },
  });

  const copyForwardMutation = useMutation({
    mutationFn: () => api.copyForwardAllocations({ fromMonth: previousMonth(month) }),
    onSuccess: async () => {
      setErrorMessage(null);
      setStatusMessage(`Copied from ${previousMonth(month)}.`);
      await queryClient.invalidateQueries({ queryKey: ['allocations', month] });
    },
    onError: (err) => {
      setStatusMessage(null);
      setErrorMessage(toMessage(err));
    },
  });

  const activePeople = people;
  const emptyMatrix = cells.length === 0;

  return (
    <AppShell>
      <div className="flex flex-col gap-2">
        <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
          Allocations
        </h1>
        <p className="text-base text-[hsl(var(--muted-foreground))]">
          Person-by-project percent for the month. Row totals must be ≤ 100.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <Label>Month</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.currentTarget.value)} />
        </div>
        <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Saving…' : 'Save'}
        </Button>
        {emptyMatrix ? (
          <Button
            variant="outline"
            onClick={() => copyForwardMutation.mutate()}
            disabled={copyForwardMutation.isPending}
          >
            {copyForwardMutation.isPending
              ? 'Copying…'
              : `Copy forward from ${previousMonth(month)}`}
          </Button>
        ) : null}
      </div>

      {errorMessage ? (
        <Alert variant="destructive">
          <AlertTitle>Failed</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      ) : null}
      {statusMessage ? (
        <Alert variant="info">
          <AlertTitle>OK</AlertTitle>
          <AlertDescription>{statusMessage}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Matrix</CardTitle>
          <CardDescription>
            {activePeople.length} people · {projects.length} active projects
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {activePeople.length === 0 || projects.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Need at least one active person and active project.
            </p>
          ) : (
            <table className="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr>
                  <th className="border-b border-[hsl(var(--border))] px-3 py-2 text-left font-medium">
                    Person
                  </th>
                  {projects.map((p) => (
                    <th
                      key={p.id}
                      className="border-b border-[hsl(var(--border))] px-3 py-2 text-right font-medium"
                    >
                      {p.code}
                    </th>
                  ))}
                  <th className="border-b border-[hsl(var(--border))] px-3 py-2 text-right font-medium">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {activePeople.map((person) => {
                  const total = rowSum(person.id);
                  const locked = lockedByPerson.has(person.id);
                  return (
                    <tr key={person.id} className="border-b border-[hsl(var(--border))]">
                      <td className="px-3 py-2">
                        {person.name}
                        {locked ? (
                          <span className="ml-2 text-xs text-[hsl(var(--muted-foreground))]">
                            locked
                          </span>
                        ) : null}
                      </td>
                      {projects.map((p) => {
                        const key = `${person.id}|${p.id}`;
                        const value = draft[key] ?? 0;
                        return (
                          <td key={p.id} className="px-3 py-2 text-right">
                            <input
                              type="number"
                              min={0}
                              max={100}
                              disabled={locked}
                              value={value}
                              onChange={(e) => {
                                const next = Number(e.currentTarget.value);
                                if (Number.isFinite(next) && next >= 0 && next <= 100) {
                                  setCell(person.id, p.id, Math.round(next));
                                }
                              }}
                              className="w-16 rounded border border-[hsl(var(--input))] px-2 py-1 text-right text-sm disabled:opacity-50"
                            />
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 text-right">
                        <Badge variant={total > 100 ? 'destructive' : 'muted'}>{total}%</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}
